import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DASHBOARD_KPI_CARD_COUNT } from "@nexus/contracts";
import { toast } from "sonner";
import { useIamPermissionForTenant } from "@/features/auth/hooks/usePermissions";
import {
  tenantSettingsQueryOptions,
  tenantSettingsKeys,
} from "@/lib/queries/tenant-settings.queries";
import type { TenantSettingsData } from "@/features/opportunities/api/tenant-settings.api";
import { getUserFacingApiMessage } from "@/lib/api-client";
import { updateTenantDashboardCards } from "@/features/identity-hub/api/tenant-kpis.api";
import {
  DEFAULT_CARD_ORDER,
  isDefaultCardOrder,
  sanitizeCardOrder,
  sanitizeDisabledCards,
} from "../lib/card-prefs";

function normalizeDisabled(raw: unknown): number[] {
  const disabled = sanitizeDisabledCards(raw);
  if (disabled.length >= DASHBOARD_KPI_CARD_COUNT) return [];
  return disabled;
}

function patchSettings(
  settings: TenantSettingsData,
  order: readonly number[],
  disabled: readonly number[],
): TenantSettingsData {
  return {
    ...settings,
    dashboardCardOrder: isDefaultCardOrder(order) ? [] : [...order],
    dashboardDisabledCards: [...disabled],
  };
}

export function useDashboardCardLayout(activeTenantId: string | null) {
  const queryClient = useQueryClient();
  const settingsQuery = useQuery(tenantSettingsQueryOptions(activeTenantId));

  const canEditPermission = useIamPermissionForTenant(
    activeTenantId ?? "",
    "IDENTITY_HUB_UPDATE_TENANT",
  );
  const canEdit =
    !!activeTenantId &&
    canEditPermission &&
    !settingsQuery.isError &&
    settingsQuery.data !== undefined;

  const order = useMemo(
    () => sanitizeCardOrder(settingsQuery.data?.dashboardCardOrder),
    [settingsQuery.data?.dashboardCardOrder],
  );
  const disabledArr = useMemo(
    () => normalizeDisabled(settingsQuery.data?.dashboardDisabledCards),
    [settingsQuery.data?.dashboardDisabledCards],
  );
  const disabledSet = useMemo(() => new Set(disabledArr), [disabledArr]);

  const saveMut = useMutation({
    mutationFn: (payload: { order: number[]; disabled: number[] }) => {
      if (!activeTenantId) throw new Error("No active tenant");
      return updateTenantDashboardCards(activeTenantId, payload);
    },
    onMutate: async (payload) => {
      if (!activeTenantId) return {};
      const key = tenantSettingsKeys.settings(activeTenantId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<TenantSettingsData>(key);
      if (previous) {
        queryClient.setQueryData(
          key,
          patchSettings(previous, payload.order, payload.disabled),
        );
      }
      return { previous };
    },
    onError: (error, _payload, context) => {
      if (activeTenantId && context?.previous) {
        queryClient.setQueryData(tenantSettingsKeys.settings(activeTenantId), context.previous);
      }
      toast.error(getUserFacingApiMessage(error));
    },
    onSettled: () => {
      if (activeTenantId) {
        void queryClient.invalidateQueries({
          queryKey: tenantSettingsKeys.settings(activeTenantId),
        });
      }
    },
  });

  const persist = useCallback(
    (nextOrder: readonly number[], nextDisabled: readonly number[]) => {
      if (!canEdit) return;
      saveMut.mutate({
        order: [...nextOrder],
        disabled: [...nextDisabled],
      });
    },
    [canEdit, saveMut],
  );

  const toggleDisabled = useCallback(
    (index: number) => {
      const next = new Set(disabledSet);
      if (next.has(index)) {
        next.delete(index);
      } else {
        if (next.size >= DASHBOARD_KPI_CARD_COUNT - 1) return;
        next.add(index);
      }
      persist(order, [...next]);
    },
    [disabledSet, order, persist],
  );

  const setOrder = useCallback(
    (nextOrder: number[]) => {
      persist(nextOrder, disabledArr);
    },
    [disabledArr, persist],
  );

  const resetLayout = useCallback(() => {
    persist([...DEFAULT_CARD_ORDER], []);
  }, [persist]);

  return {
    order,
    disabledSet,
    isLoading: settingsQuery.isLoading,
    isError: settingsQuery.isError,
    canEdit,
    isOrderCustomized: !isDefaultCardOrder(order) || disabledArr.length > 0,
    isSaving: saveMut.isPending,
    toggleDisabled,
    setOrder,
    resetLayout,
  };
}
